import { useMemo, useState } from "react";
import { Cloud, Database, Shield, ArrowRight, RefreshCw, PlugZap, Loader2, DownloadCloud } from "lucide-react";
import { toast } from "sonner";
import API from "../lib/api";
import { useAudit } from "../context/AuditContext";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";

const INTEGRATIONS = [
  {
    id: "aws-s3",
    name: "AWS S3 Cloud",
    description: "Archive vault & unstructured logs",
    status: "Connected",
    statusTone: "emerald",
    metaLabel: "Last Synced",
    metaValue: "14:02:11 GMT",
    action: "Configure",
    icon: Cloud,
    iconBg: "bg-orange-500/15",
    iconColor: "text-orange-300",
  },
  {
    id: "sql-mainframe",
    name: "SQL Mainframe",
    description: "Transactional core DB (read-only)",
    status: "Idle",
    statusTone: "amber",
    metaLabel: "Last Synced",
    metaValue: "09:14:00 GMT",
    action: "Manual Push",
    icon: Database,
    iconBg: "bg-indigo-400/15",
    iconColor: "text-indigo-200",
  },
  {
    id: "ledger-api",
    name: "External Ledger API",
    description: "Real-time settlement gateway",
    status: "Ready",
    statusTone: "amber",
    metaLabel: "Endpoint",
    metaValue: "get_inputs",
    action: "Sync",
    icon: Shield,
    iconBg: "bg-cyan-400/15",
    iconColor: "text-cyan-200",
    actionPrimary: true,
  },
];

const STATUS_STYLES = {
  emerald: "bg-emerald-400/10 text-emerald-200 border-emerald-300/40",
  amber: "bg-amber-300/10 text-amber-200 border-amber-200/40",
  rose: "bg-rose-300/10 text-rose-200 border-rose-200/40",
};

export default function Integrations() {
  const { auditRun } = useAudit();
  const [s3Bucket, setS3Bucket] = useState("");
  const [s3Prefix, setS3Prefix] = useState("");
  const [s3Objects, setS3Objects] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [manualS3Keys, setManualS3Keys] = useState("");
  const [s3Loading, setS3Loading] = useState(false);
  const [s3Importing, setS3Importing] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerPayload, setLedgerPayload] = useState(null);
  const selectedCount = selectedKeys.length;
  const selectedSize = useMemo(
    () => s3Objects.filter((item) => selectedKeys.includes(item.key)).reduce((sum, item) => sum + (Number(item.size) || 0), 0),
    [s3Objects, selectedKeys]
  );
  const ledgerRows = Array.isArray(ledgerPayload?.results) ? ledgerPayload.results : [];
  const manualKeys = useMemo(
    () => manualS3Keys.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean),
    [manualS3Keys]
  );

  const formatBytes = (value) => {
    const size = Number(value) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadS3Objects = async () => {
    if (!auditRun?.id) {
      toast.error("Select or create an audit run first.");
      return;
    }
    setS3Loading(true);
    try {
      const params = {};
      if (s3Bucket.trim()) params.bucket = s3Bucket.trim();
      if (s3Prefix.trim()) params.prefix = s3Prefix.trim();
      const { data } = await API.get(`/audit-runs/${auditRun.id}/integrations/s3/objects`, { params });
      setS3Objects(Array.isArray(data?.objects) ? data.objects : []);
      setSelectedKeys([]);
      if (data?.bucket && !s3Bucket.trim()) setS3Bucket(data.bucket);
      toast.success(`Loaded ${(data?.objects || []).length} S3 artifact(s)`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || detail || "Failed to list S3 artifacts");
    } finally {
      setS3Loading(false);
    }
  };

  const toggleKey = (key) => {
    setSelectedKeys((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  };

  const importSelected = async () => {
    if (!auditRun?.id) {
      toast.error("Select or create an audit run first.");
      return;
    }
    if (selectedKeys.length === 0) {
      toast.error("Select at least one S3 object.");
      return;
    }
    setS3Importing(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/integrations/s3/import`, {
        bucket: s3Bucket.trim() || undefined,
        prefix: s3Prefix.trim() || undefined,
        keys: selectedKeys,
      });
      toast.success(`Imported ${data?.imported_count || 0} artifact(s) from S3`);
      await loadS3Objects();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to import S3 artifacts");
    } finally {
      setS3Importing(false);
    }
  };

  const importManualKeys = async () => {
    if (!auditRun?.id) {
      toast.error("Select or create an audit run first.");
      return;
    }
    if (manualKeys.length === 0) {
      toast.error("Paste at least one S3 object key.");
      return;
    }
    setS3Importing(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/integrations/s3/import`, {
        bucket: s3Bucket.trim() || undefined,
        prefix: s3Prefix.trim() || undefined,
        keys: manualKeys,
      });
      toast.success(`Imported ${data?.imported_count || 0} artifact(s) from S3`);
      setManualS3Keys("");
      setSelectedKeys([]);
      setS3Objects((current) => current.map((item) => manualKeys.includes(item.key) ? { ...item, imported: true } : item));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || detail || "Failed to import S3 artifacts");
    } finally {
      setS3Importing(false);
    }
  };

  const loadLedgerInputs = async () => {
    if (!auditRun?.id) {
      toast.error("Select or create an audit run first.");
      return;
    }
    setLedgerLoading(true);
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/integrations/ledger/inputs`);
      setLedgerPayload(data);
      toast.success(`Synced ${data?.total ?? data?.results?.length ?? 0} ledger input(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to sync external ledger inputs");
    } finally {
      setLedgerLoading(false);
    }
  };

  const getIntegrationState = (integration) => {
    if (integration.id === "ledger-api") {
      if (ledgerLoading) {
        return { ...integration, status: "Syncing", statusTone: "amber", metaLabel: "Status", metaValue: "Fetching inputs" };
      }
      if (ledgerRows.length > 0) {
        return { ...integration, status: "Connected", statusTone: "emerald", metaLabel: "Records", metaValue: String(ledgerRows.length) };
      }
    }
    return integration;
  };

  return (
    <section className="relative overflow-hidden rounded-[32px] bg-[#0c0e12] px-8 py-10 text-slate-100 shadow-[0_30px_80px_rgba(8,10,16,0.45)]" data-testid="integrations-page">
      <div className="pointer-events-none absolute -left-20 top-10 h-64 w-64 rounded-full bg-indigo-500/20 blur-[120px]" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-orange-500/20 blur-[140px]" />
      <div className="pointer-events-none absolute left-1/3 top-0 h-40 w-40 rounded-full bg-cyan-400/10 blur-[90px]" />

      <div className="relative z-10 mx-auto flex max-w-5xl flex-col gap-10">
        <header className="flex flex-col gap-4">
          <span className="text-xs uppercase tracking-[0.4em] text-orange-200/70">Integrations</span>
          <div>
            <h1 className="text-4xl font-semibold tracking-tight text-white">
              Active <span className="text-orange-300 italic">Channels</span>
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-300/90">
              Streamline high-velocity financial data across AWS S3, SQL mainframes, and external APIs. Pull data from anywhere,
              validate it in near real time, and keep every feed audit-ready.
            </p>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-orange-300/20 bg-white/5 px-5 py-4 shadow-[0_16px_30px_rgba(0,0,0,0.18)]">
            <p className="text-[10px] uppercase tracking-[0.28em] text-orange-200/60">Health Score</p>
            <p className="mt-2 text-3xl font-semibold text-white">98.4%</p>
            <p className="mt-1 text-xs text-slate-400">Across all connected sources</p>
          </div>
          <div className="rounded-2xl border border-indigo-300/20 bg-white/5 px-5 py-4 shadow-[0_16px_30px_rgba(0,0,0,0.18)]">
            <p className="text-[10px] uppercase tracking-[0.28em] text-indigo-200/60">Active Sync</p>
            <p className="mt-2 text-3xl font-semibold text-white">03</p>
            <p className="mt-1 text-xs text-slate-400">Feeds streaming data now</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {INTEGRATIONS.map((integration) => {
            const display = getIntegrationState(integration);
            const Icon = display.icon;
            return (
              <div
                key={display.id}
                className="group relative overflow-hidden rounded-[28px] border border-white/10 bg-white/5 p-6 shadow-[0_20px_40px_rgba(0,0,0,0.25)] transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
              >
                <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-white/5 opacity-40 transition-opacity duration-300 group-hover:opacity-70" />
                <div className="relative z-10 flex items-start justify-between gap-3">
                  <div className={`rounded-2xl p-3 ${display.iconBg}`}>
                    <Icon className={`h-6 w-6 ${display.iconColor}`} />
                  </div>
                  <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] ${STATUS_STYLES[display.statusTone]}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {display.status}
                  </div>
                </div>

                <div className="relative z-10 mt-8">
                  <h3 className="text-xl font-semibold text-white">{display.name}</h3>
                  <p className="mt-1 text-sm text-slate-300/80">{display.description}</p>
                </div>

                <div className="relative z-10 mt-6 flex items-end justify-between border-t border-white/10 pt-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-slate-400">{display.metaLabel}</p>
                    <p className={`mt-1 text-xs font-semibold ${display.statusTone === "rose" ? "text-rose-200" : "text-slate-200"}`}>
                      {display.metaValue}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={display.id === "aws-s3" ? loadS3Objects : display.id === "ledger-api" ? loadLedgerInputs : undefined}
                    disabled={(display.id === "aws-s3" && s3Loading) || (display.id === "ledger-api" && ledgerLoading)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] transition-all ${
                      display.actionPrimary
                        ? "bg-orange-400 text-slate-950 shadow-[0_12px_24px_rgba(249,115,22,0.35)] hover:bg-orange-300"
                        : "text-indigo-200 hover:text-white"
                    } disabled:opacity-50`}
                  >
                    {display.action}
                    {(s3Loading && display.id === "aws-s3") || (ledgerLoading && display.id === "ledger-api") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : display.actionPrimary ? <ArrowRight className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 shadow-[0_20px_40px_rgba(0,0,0,0.25)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.28em] text-orange-200/60">AWS S3 Artifact Sync</p>
              <h2 className="mt-2 text-xl font-semibold text-white">Import CSVs and evidence artifacts into this audit</h2>
              <p className="mt-1 text-sm text-slate-300/80">
                Files imported here become normal audit artifacts, so Phase 7 can run SQL, OCR, and evidence evaluation over them.
              </p>
            </div>
            <Badge variant="outline" className="w-fit border-orange-300/30 bg-orange-300/10 text-orange-100">
              {auditRun?.name ? `Active audit: ${auditRun.name}` : "No active audit"}
            </Badge>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
            <Input
              value={s3Bucket}
              onChange={(event) => setS3Bucket(event.target.value)}
              placeholder="Bucket from backend env, or enter bucket name"
              className="border-white/10 bg-slate-950/60 text-slate-100 placeholder:text-slate-500"
            />
            <Input
              value={s3Prefix}
              onChange={(event) => setS3Prefix(event.target.value)}
              placeholder="Optional prefix, e.g. audits/2026/testthree/"
              className="border-white/10 bg-slate-950/60 text-slate-100 placeholder:text-slate-500"
            />
            <button
              type="button"
              onClick={loadS3Objects}
              disabled={s3Loading || !auditRun?.id}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-orange-300/30 px-5 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-orange-100 transition hover:bg-orange-300/10 disabled:opacity-50"
            >
              {s3Loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Browse
            </button>
          </div>

          <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/45">
            {s3Objects.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">
                Browse your bucket to list supported files: CSV, Excel, PDF, Word, text, and image artifacts.
              </div>
            ) : (
              <div className="max-h-72 overflow-auto">
                {s3Objects.map((item) => (
                  <label key={item.key} className="flex cursor-pointer items-center gap-3 border-b border-white/10 px-4 py-3 text-sm last:border-b-0 hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(item.key)}
                      onChange={() => toggleKey(item.key)}
                      disabled={item.imported}
                      className="h-4 w-4 accent-orange-400"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-100">{item.filename}</p>
                      <p className="truncate text-xs text-slate-500">{item.key}</p>
                    </div>
                    <div className="shrink-0 text-right text-xs text-slate-400">
                      <p>{formatBytes(item.size)}</p>
                      {item.imported && <p className="text-emerald-300">Imported</p>}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/45 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-300">Direct import</p>
            <p className="mt-1 text-xs text-slate-500">
              If bucket browsing is blocked, paste exact S3 object keys here, one per line or comma-separated.
            </p>
            <Textarea
              value={manualS3Keys}
              onChange={(event) => setManualS3Keys(event.target.value)}
              placeholder="audits/2026/testthree/evidence.csv"
              className="mt-3 min-h-24 border-white/10 bg-slate-950/60 text-slate-100 placeholder:text-slate-500"
            />
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-400">
                {manualKeys.length ? `${manualKeys.length} key(s) ready to import.` : "Use this when IAM allows GetObject but not ListBucket."}
              </p>
              <button
                type="button"
                onClick={importManualKeys}
                disabled={s3Importing || manualKeys.length === 0 || !auditRun?.id}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-orange-300/30 px-5 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-orange-100 transition hover:bg-orange-300/10 disabled:opacity-50"
              >
                {s3Importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />}
                Import Keys
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-400">
              {selectedCount ? `${selectedCount} selected (${formatBytes(selectedSize)})` : "Select S3 objects to import as artifacts."}
            </p>
            <button
              type="button"
              onClick={importSelected}
              disabled={s3Importing || selectedCount === 0 || !auditRun?.id}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-400 px-5 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-950 transition hover:bg-orange-300 disabled:opacity-50"
            >
              {s3Importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />}
              Import Selected
            </button>
          </div>
        </div>

        <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 shadow-[0_20px_40px_rgba(0,0,0,0.25)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-cyan-200/60">External Ledger API</p>
              <h2 className="mt-2 text-xl font-semibold text-white">Sync insurance application inputs</h2>
              <p className="mt-1 text-sm text-slate-300/80">
                Pulls records from the backend API proxy and shows the latest application, policy, and document metadata.
              </p>
            </div>
            <button
              type="button"
              onClick={loadLedgerInputs}
              disabled={ledgerLoading || !auditRun?.id}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-400 px-5 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-950 transition hover:bg-orange-300 disabled:opacity-50"
            >
              {ledgerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Sync Inputs
            </button>
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/45">
            {ledgerRows.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">
                Sync the ledger API to preview application inputs here.
              </div>
            ) : (
              <div className="max-h-80 overflow-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="sticky top-0 bg-slate-950 text-[10px] uppercase tracking-[0.24em] text-slate-400">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Application</th>
                      <th className="px-4 py-3 font-semibold">Applicant</th>
                      <th className="px-4 py-3 font-semibold">Plan</th>
                      <th className="px-4 py-3 font-semibold">Claims</th>
                      <th className="px-4 py-3 font-semibold">Sum Assured</th>
                      <th className="px-4 py-3 font-semibold">Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerRows.map((row, index) => {
                      const appId = row["Application ID"] || `APP-${index + 1}`;
                      const docs = [row.income_certificate_file, row.kyc_document_file, row.lab_report_file].filter(Boolean);
                      return (
                        <tr key={`${appId}-${index}`} className="border-t border-white/10">
                          <td className="px-4 py-3 font-mono text-xs text-cyan-100">{appId}</td>
                          <td className="px-4 py-3 text-slate-100">{row.name || "-"}</td>
                          <td className="px-4 py-3 text-slate-300">{row["Plan Type"] || "-"}</td>
                          <td className="px-4 py-3 text-slate-300">{row["Claims Propensity"] || "-"}</td>
                          <td className="px-4 py-3 text-slate-300">
                            {row.sum_assured ? Number(row.sum_assured).toLocaleString() : "-"}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400">{docs.length ? docs.join(", ") : "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            className="flex w-full max-w-lg items-center justify-center gap-3 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.35em] text-slate-950 shadow-[0_20px_40px_rgba(249,115,22,0.35)] transition-all hover:translate-y-[-2px]"
          >
            <PlugZap className="h-5 w-5" />
            Connect New Source
          </button>
          <p className="text-xs text-slate-400">Securely link any data source, including custom ledgers and third-party APIs.</p>
        </div>
      </div>
    </section>
  );
}
